import React, { useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useSearch } from "../context/SearchContext";
import {
  FaSearch,
  FaSpinner,
  FaExternalLinkAlt,
  FaInfoCircle,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import "../styles/SearchPage.css";

// Helper function to create section IDs - must match CheatSheetContent.js exactly
const createSectionId = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric characters with hyphens
    .replace(/^-+|-+$/g, "") // Remove leading/trailing hyphens
    .replace(/\s+/g, "-"); // Replace spaces with hyphens
};

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const { searchResults, isLoading, error, search, searchGoogle } = useSearch();

  useEffect(() => {
    if (query) {
      search(query);
    }
  }, [query, search]);

  const handleResultClick = (e, link) => {
    e.preventDefault();
    const [path, hash] = link.split("#");

    // Navigate to the page first
    navigate(path, { replace: true });

    // Then try to scroll to the section
    const scrollToSection = () => {
      // Special handling for HTML cheatsheet
      const isHtmlCheatsheet =
        path.includes("/html5") || path.includes("/cheatsheet/html5");

      // Try multiple ways to find the section
      const element =
        document.getElementById(hash) ||
        document.querySelector(`[data-section-id="${hash}"]`) ||
        document.querySelector(`[data-item-id="${hash}"]`) ||
        document.querySelector(`[id*="${hash}"]`) || // Try partial ID match
        document.querySelector(`[data-section-id*="${hash}"]`) || // Try partial section ID
        document.querySelector(`[data-item-id*="${hash}"]`); // Try partial item ID

      if (element) {
        // Ensure the section is expanded
        const section =
          element.closest(".section, .html-section, .cheatsheet-section") ||
          element;
        if (section) {
          // Remove any collapsed classes
          section.classList.remove("collapsed");
          // Add expanded class
          section.classList.add("expanded");
          // Show all child elements
          const children = section.querySelectorAll(
            ".section-content, .item-content, .html-section, .html-item, .cheatsheet-content, .cheatsheet-item"
          );
          children.forEach((child) => {
            child.style.display = "block";
            child.classList.remove("collapsed");
            child.classList.add("expanded");
            // Also expand any nested content
            const nestedContent = child.querySelectorAll(
              ".content, .item-content, .section-content"
            );
            nestedContent.forEach((nested) => {
              nested.style.display = "block";
              nested.classList.remove("collapsed");
              nested.classList.add("expanded");
            });
          });

          // If it's an HTML section, also expand the parent section
          if (isHtmlCheatsheet) {
            const parentSection = section.closest(
              ".cheatsheet-section, .html-section"
            );
            if (parentSection) {
              parentSection.classList.remove("collapsed");
              parentSection.classList.add("expanded");
              const parentContent = parentSection.querySelectorAll(
                ".cheatsheet-content, .html-section"
              );
              parentContent.forEach((content) => {
                content.style.display = "block";
                content.classList.remove("collapsed");
                content.classList.add("expanded");
              });
            }
          }
        }

        element.scrollIntoView({ behavior: "smooth" });
        element.classList.add("highlight");
        setTimeout(() => element.classList.remove("highlight"), 2000);
      } else {
        console.warn(
          `Section not found: ${hash}. Trying alternative selectors...`
        );

        // Try alternative selectors with different formats
        const alternativeSelectors = [
          // HTML specific selectors
          ...(isHtmlCheatsheet
            ? [
                `#html-${hash}`,
                `#html5-${hash}`,
                `[id*="html-${hash}"]`,
                `[id*="html5-${hash}"]`,
                `[data-section-id*="html-${hash}"]`,
                `[data-section-id*="html5-${hash}"]`,
                `.html-section[data-section-id*="${hash}"]`,
                `.html-item[data-item-id*="${hash}"]`,
                `.cheatsheet-section[data-section-id*="${hash}"]`,
                `.cheatsheet-item[data-item-id*="${hash}"]`,
              ]
            : []),
          // General selectors
          `#${hash.replace(/-/g, "_")}`, // Try with underscores
          `#${hash.replace(/_/g, "-")}`, // Try with hyphens
          `[id*="${hash.replace(/-/g, "")}"]`, // Try without separators
          `[data-section-id*="${hash.replace(/-/g, "")}"]`,
          `[data-item-id*="${hash.replace(/-/g, "")}"]`,
          `[id*="${hash.toLowerCase()}"]`, // Try lowercase
          `[data-section-id*="${hash.toLowerCase()}"]`,
          `[data-item-id*="${hash.toLowerCase()}"]`,
          `[id*="${hash.replace(/\s+/g, "-")}"]`, // Try with spaces replaced
          `[data-section-id*="${hash.replace(/\s+/g, "-")}"]`,
          `[data-item-id*="${hash.replace(/\s+/g, "-")}"]`,
          // Add more specific selectors for common cases
          `#${hash}-section`,
          `#${hash}-item`,
          `[id*="${hash}-section"]`,
          `[id*="${hash}-item"]`,
          `[data-section-id*="${hash}-section"]`,
          `[data-item-id*="${hash}-item"]`,
          // Try with common prefixes/suffixes
          `#section-${hash}`,
          `#item-${hash}`,
          `[id*="section-${hash}"]`,
          `[id*="item-${hash}"]`,
          `[data-section-id*="section-${hash}"]`,
          `[data-item-id*="item-${hash}"]`,
        ];

        for (const selector of alternativeSelectors) {
          const altElement = document.querySelector(selector);
          if (altElement) {
            // Ensure the section is expanded
            const section =
              altElement.closest(
                ".section, .html-section, .cheatsheet-section"
              ) || altElement;
            if (section) {
              // Remove any collapsed classes
              section.classList.remove("collapsed");
              // Add expanded class
              section.classList.add("expanded");
              // Show all child elements
              const children = section.querySelectorAll(
                ".section-content, .item-content, .html-section, .html-item, .cheatsheet-content, .cheatsheet-item"
              );
              children.forEach((child) => {
                child.style.display = "block";
                child.classList.remove("collapsed");
                child.classList.add("expanded");
                // Also expand any nested content
                const nestedContent = child.querySelectorAll(
                  ".content, .item-content, .section-content"
                );
                nestedContent.forEach((nested) => {
                  nested.style.display = "block";
                  nested.classList.remove("collapsed");
                  nested.classList.add("expanded");
                });
              });

              // If it's an HTML section, also expand the parent section
              if (isHtmlCheatsheet) {
                const parentSection = section.closest(
                  ".cheatsheet-section, .html-section"
                );
                if (parentSection) {
                  parentSection.classList.remove("collapsed");
                  parentSection.classList.add("expanded");
                  const parentContent = parentSection.querySelectorAll(
                    ".cheatsheet-content, .html-section"
                  );
                  parentContent.forEach((content) => {
                    content.style.display = "block";
                    content.classList.remove("collapsed");
                    content.classList.add("expanded");
                  });
                }
              }
            }

            altElement.scrollIntoView({ behavior: "smooth" });
            altElement.classList.add("highlight");
            setTimeout(() => altElement.classList.remove("highlight"), 2000);
            return;
          }
        }

        // If still not found, try to find any element with similar text content
        const allElements = document.querySelectorAll(
          "h1, h2, h3, h4, h5, h6, [data-section-id], [data-item-id], .section-title, .item-title, .html-section-title, .html-item-title, .cheatsheet-section-title, .cheatsheet-item-title"
        );
        for (const el of allElements) {
          if (
            el.textContent.toLowerCase().includes(hash.toLowerCase()) ||
            el.textContent
              .toLowerCase()
              .replace(/\s+/g, "-")
              .includes(hash.toLowerCase())
          ) {
            // Ensure the section is expanded
            const section =
              el.closest(".section, .html-section, .cheatsheet-section") || el;
            if (section) {
              // Remove any collapsed classes
              section.classList.remove("collapsed");
              // Add expanded class
              section.classList.add("expanded");
              // Show all child elements
              const children = section.querySelectorAll(
                ".section-content, .item-content, .html-section, .html-item, .cheatsheet-content, .cheatsheet-item"
              );
              children.forEach((child) => {
                child.style.display = "block";
                child.classList.remove("collapsed");
                child.classList.add("expanded");
                // Also expand any nested content
                const nestedContent = child.querySelectorAll(
                  ".content, .item-content, .section-content"
                );
                nestedContent.forEach((nested) => {
                  nested.style.display = "block";
                  nested.classList.remove("collapsed");
                  nested.classList.add("expanded");
                });
              });

              // If it's an HTML section, also expand the parent section
              if (isHtmlCheatsheet) {
                const parentSection = section.closest(
                  ".cheatsheet-section, .html-section"
                );
                if (parentSection) {
                  parentSection.classList.remove("collapsed");
                  parentSection.classList.add("expanded");
                  const parentContent = parentSection.querySelectorAll(
                    ".cheatsheet-content, .html-section"
                  );
                  parentContent.forEach((content) => {
                    content.style.display = "block";
                    content.classList.remove("collapsed");
                    content.classList.add("expanded");
                  });
                }
              }
            }

            el.scrollIntoView({ behavior: "smooth" });
            el.classList.add("highlight");
            setTimeout(() => el.classList.remove("highlight"), 2000);
            return;
          }
        }

        // If still not found, try to find the closest parent section
        const parentSection = document.querySelector(
          `[data-section-id*="${hash.split("-")[0]}"]`
        );
        if (parentSection) {
          // Ensure the section is expanded
          const section =
            parentSection.closest(
              ".section, .html-section, .cheatsheet-section"
            ) || parentSection;
          if (section) {
            // Remove any collapsed classes
            section.classList.remove("collapsed");
            // Add expanded class
            section.classList.add("expanded");
            // Show all child elements
            const children = section.querySelectorAll(
              ".section-content, .item-content, .html-section, .html-item, .cheatsheet-content, .cheatsheet-item"
            );
            children.forEach((child) => {
              child.style.display = "block";
              child.classList.remove("collapsed");
              child.classList.add("expanded");
              // Also expand any nested content
              const nestedContent = child.querySelectorAll(
                ".content, .item-content, .section-content"
              );
              nestedContent.forEach((nested) => {
                nested.style.display = "block";
                nested.classList.remove("collapsed");
                nested.classList.add("expanded");
              });
            });

            // If it's an HTML section, also expand the parent section
            if (isHtmlCheatsheet) {
              const parentSection = section.closest(
                ".cheatsheet-section, .html-section"
              );
              if (parentSection) {
                parentSection.classList.remove("collapsed");
                parentSection.classList.add("expanded");
                const parentContent = parentSection.querySelectorAll(
                  ".cheatsheet-content, .html-section"
                );
                parentContent.forEach((content) => {
                  content.style.display = "block";
                  content.classList.remove("collapsed");
                  content.classList.add("expanded");
                });
              }
            }
          }

          parentSection.scrollIntoView({ behavior: "smooth" });
          parentSection.classList.add("highlight");
          setTimeout(() => parentSection.classList.remove("highlight"), 2000);
          return;
        }
      }
    };

    // Try multiple times with increasing delays
    setTimeout(scrollToSection, 100);
    setTimeout(scrollToSection, 500);
    setTimeout(scrollToSection, 1000);
    setTimeout(scrollToSection, 2000); // Add one more attempt with longer delay
  };

  const renderHighlightedText = (text, query) => {
    if (!text || !query) return text;
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i}>{part}</mark>
      ) : (
        part
      )
    );
  };

  const renderContent = () => {
    if (error) {
      return (
        <div className="search-error">
          <h2>Search Error</h2>
          <p>{error}</p>
          <button onClick={() => search(query)}>Try Again</button>
        </div>
      );
    }

    // Log the search results for debugging
    console.log(
      "Rendering search results:",
      JSON.stringify(
        searchResults.slice(0, 3).map((r) => ({
          id: r.id,
          title: r.title,
          content: r.content?.substring(0, 50) + "...",
          tags: r.tags,
          type: r.type,
          category: r.category,
          section: r.section,
          subsection: r.subsection,
          link: r.link,
        })),
        null,
        2
      )
    );

    return (
      <div className="search-results-container">
        {isLoading ? (
          <div className="loading-state">
            <FaSpinner className="spinner" />
            <p>Searching...</p>
          </div>
        ) : searchResults.length > 0 ? (
          <>
            <h2 className="results-header">
              Found {searchResults.length} results for "{query}"
            </h2>
            <div className="search-results">
              {searchResults.map((result) => {
                // Log each result for debugging
                console.log("Processing result:", {
                  id: result.id,
                  title: result.title,
                  hasContent: !!result.content,
                  contentLength: result.content?.length,
                  hasTags: !!result.tags,
                  tagsLength: result.tags?.length,
                });

                // Ensure all required properties have default values
                const safeResult = {
                  id: result.id || `${result.category}-${result.title}`,
                  title: result.title || "",
                  category: result.category || "",
                  content: result.content || "",
                  section: result.section || "",
                  subsection: result.subsection || "",
                  note: result.note || "",
                  tags: result.tags || [],
                  link: result.link || "",
                };

                return (
                  <Link
                    key={safeResult.id}
                    to={safeResult.link}
                    className="search-result-item"
                    onClick={(e) => handleResultClick(e, safeResult.link)}
                  >
                    <div className="result-header">
                      <h3>{renderHighlightedText(safeResult.title, query)}</h3>
                      <span className="result-category">
                        {safeResult.category}
                      </span>
                    </div>
                    {safeResult.section && (
                      <div className="result-section">
                        {renderHighlightedText(safeResult.section, query)}
                        {safeResult.subsection && (
                          <>
                            {" "}
                            ›{" "}
                            {renderHighlightedText(
                              safeResult.subsection,
                              query
                            )}
                          </>
                        )}
                      </div>
                    )}
                    <div className="result-content">
                      {renderHighlightedText(
                        safeResult.content.slice(0, 200) +
                          (safeResult.content.length > 200 ? "..." : ""),
                        query
                      )}
                      {safeResult.note && (
                        <div className="result-note">
                          <FaInfoCircle className="note-icon" />
                          {renderHighlightedText(safeResult.note, query)}
                        </div>
                      )}
                    </div>
                    <div className="result-meta">
                      <div className="result-tags">
                        {safeResult.tags.map((tag, index) => (
                          <span key={index} className="tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        ) : query ? (
          <div className="no-results">
            <FaSearch className="no-results-icon" />
            <h2>No results found for "{query}"</h2>
            <p>Try different keywords or check our available cheatsheets:</p>
            <div className="cheatsheet-suggestions">
              {["CSS", "Git", "PHP", "SQL", "UX/UI"].map((sheet) => (
                <Link
                  key={sheet}
                  to={`/${sheet.toLowerCase().replace("/", "-")}`}
                  className="suggestion-link"
                >
                  {sheet}
                </Link>
              ))}
            </div>
            <button
              className="google-search-button"
              onClick={() => searchGoogle(query)}
            >
              Search on Google <FaExternalLinkAlt />
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="page-container">
      <Navbar />
      <main className="search-page">{renderContent()}</main>
    </div>
  );
};

export default SearchPage;
